import React, { useLayoutEffect, useRef, useState } from 'react';
import { useDroppable, useDraggable } from '@dnd-kit/core';
import { getCardImageUri } from '@mtg-deck-tool/shared';
import { usePlaytesterStore } from '../../stores/playtester-store.js';
import CommandZoneDisplay from './CommandZoneDisplay.js';
import HandDisplay from './HandDisplay.js';
import { CARD_CORNER_RADIUS } from '../../lib/playtester-engine.js';

// Real Magic card back (Scryfall-hosted, high-res normal size)
const CARD_BACK_URL =
  'https://backs.scryfall.io/normal/0/a/0aeebaf5-8c7d-4636-9e82-8c27447861f7.jpg';

// ── Library context menu ───────────────────────────────────────────────────────

interface LibraryContextMenuProps {
  x: number;
  y: number;
  onClose: () => void;
}

function LibraryContextMenu({ x, y, onClose }: LibraryContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<React.CSSProperties>({
    position: 'fixed',
    left: x,
    top: y,
    visibility: 'hidden',
  });

  const drawCard = usePlaytesterStore((s) => s.drawCard);
  const shuffleLibrary = usePlaytesterStore((s) => s.shuffleLibrary);
  const showTopCard = usePlaytesterStore((s) => s.showTopCard);
  const toggleShowTopCard = usePlaytesterStore((s) => s.toggleShowTopCard);
  const setOpenPanel = usePlaytesterStore((s) => s.setOpenPanel);
  const openScry = usePlaytesterStore((s) => s.openScry);
  const millTopX = usePlaytesterStore((s) => s.millTopX);
  const library = usePlaytesterStore((s) => s.library);

  const [viewTopX, setViewTopX] = useState(3);
  const [millX, setMillX] = useState(1);

  // Clamp to viewport after first render
  useLayoutEffect(() => {
    const el = menuRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = x;
    let top = y;
    if (left + rect.width > vw - 4) left = vw - rect.width - 4;
    if (top + rect.height > vh - 4) top = vh - rect.height - 4;
    if (left < 4) left = 4;
    if (top < 4) top = 4;
    setStyle({ position: 'fixed', left, top, visibility: 'visible' });
  }, [x, y]);

  function close() { onClose(); }

  function handleDraw() {
    drawCard();
    close();
  }

  function handleShuffle() {
    shuffleLibrary();
    close();
  }

  function handleToggleReveal() {
    toggleShowTopCard();
    close();
  }

  function handleViewTop() {
    setOpenPanel('library');
    close();
  }

  function handleViewTopX() {
    openScry(viewTopX);
    close();
  }

  function handleMill() {
    millTopX(millX);
    close();
  }

  return (
    <>
      {/* Backdrop to close */}
      <div className="fixed inset-0 z-[140]" onClick={close} />
      <div
        ref={menuRef}
        style={style}
        className="z-[150] bg-gray-900 border border-gray-700 rounded shadow-2xl py-1 min-w-[170px]"
      >
        <button
          onClick={handleDraw}
          className="w-full text-left px-3 py-1.5 text-sm text-gray-200 hover:bg-gray-700 transition"
        >
          Draw
        </button>
        <button
          onClick={handleShuffle}
          className="w-full text-left px-3 py-1.5 text-sm text-gray-200 hover:bg-gray-700 transition"
        >
          Shuffle
        </button>
        <button
          onClick={handleToggleReveal}
          className="w-full text-left px-3 py-1.5 text-sm text-gray-200 hover:bg-gray-700 transition"
        >
          {showTopCard ? 'Hide Top Card' : 'Play with Top Revealed'}
        </button>
        <button
          onClick={handleViewTop}
          className="w-full text-left px-3 py-1.5 text-sm text-gray-200 hover:bg-gray-700 transition"
        >
          View Library
        </button>
        <div className="border-t border-gray-700 my-1" />
        <div className="px-3 py-1.5 flex items-center gap-2">
          <span className="text-sm text-gray-400 shrink-0">View Top</span>
          <input
            type="number"
            min={1}
            max={library.length || 1}
            value={viewTopX}
            onChange={(e) => setViewTopX(Math.max(1, parseInt(e.target.value, 10) || 1))}
            onClick={(e) => e.stopPropagation()}
            className="w-12 text-center text-xs bg-gray-800 border border-gray-700 rounded text-white outline-none focus:border-orange-500"
          />
          <button
            onClick={handleViewTopX}
            className="text-xs px-2 py-0.5 bg-gray-700 hover:bg-gray-600 rounded text-gray-200 transition"
          >
            Go
          </button>
        </div>
        <div className="border-t border-gray-700 my-1" />
        <div className="px-3 py-1.5 flex items-center gap-2">
          <span className="text-sm text-gray-400 shrink-0">Mill</span>
          <input
            type="number"
            min={1}
            max={library.length || 1}
            value={millX}
            onChange={(e) => setMillX(Math.max(1, parseInt(e.target.value, 10) || 1))}
            onClick={(e) => e.stopPropagation()}
            className="w-12 text-center text-xs bg-gray-800 border border-gray-700 rounded text-white outline-none focus:border-orange-500"
          />
          <button
            onClick={handleMill}
            className="text-xs px-2 py-0.5 bg-gray-700 hover:bg-gray-600 rounded text-gray-200 transition"
          >
            Go
          </button>
        </div>
      </div>
    </>
  );
}

// ── LibraryZoneBadge ──────────────────────────────────────────────────────────

function LibraryZoneBadge() {
  const library = usePlaytesterStore((s) => s.library);
  const showTopCard = usePlaytesterStore((s) => s.showTopCard);
  const setOpenPanel = usePlaytesterStore((s) => s.setOpenPanel);
  const setHoveredZone = usePlaytesterStore((s) => s.setHoveredZone);
  const { setNodeRef, isOver } = useDroppable({ id: 'library' });

  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number } | null>(null);

  const topCard = library[0];
  // When "Play with Top Revealed" is on, show the top card image; otherwise show card back
  const artUri =
    topCard && showTopCard ? getCardImageUri(topCard.card, 'large') : undefined;

  // The top card is draggable straight off the deck, as on the graveyard and
  // exile piles. Works whether or not the top card is revealed.
  const { setNodeRef: setDragRef, listeners, attributes, isDragging } = useDraggable({
    // Distinct from the library panel's row for the same card: two draggables
    // sharing one id collide while both are mounted. Handlers read
    // data.instanceId, not the drag id, so a prefixed id is safe.
    id: topCard ? `library-pile-${topCard.instanceId}` : 'library-empty',
    data: topCard ? { instanceId: topCard.instanceId, zone: 'library' } : {},
    disabled: !topCard,
  });

  function handleContextMenu(e: React.MouseEvent) {
    e.preventDefault();
    setCtxMenu({ x: e.clientX, y: e.clientY });
  }

  return (
    <div
      ref={setNodeRef}
      className={`flex flex-col items-center gap-1 transition ${isOver ? 'opacity-80 ring-1 ring-blue-500 rounded' : ''}`}
    >
      <div
        ref={setDragRef}
        {...listeners}
        {...attributes}
        onClick={() => setOpenPanel('library')}
        onContextMenu={handleContextMenu}
        onMouseEnter={() => setHoveredZone('library')}
        onMouseLeave={() => setHoveredZone(null)}
        className={`relative group shrink-0 cursor-pointer ${isDragging ? 'opacity-40' : ''}`}
        style={{ width: 'var(--pt-card-w)', height: 'var(--pt-card-h)' }}
        title={`Library (${library.length}) — drag top card, right-click for options`}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter') setOpenPanel('library'); }}
      >
        {artUri ? (
          <img
            src={artUri}
            alt="Top card"
            className="w-full h-full object-cover rounded-sm border border-gray-700 group-hover:border-orange-500 transition"
            draggable={false} style={{ borderRadius: CARD_CORNER_RADIUS }} />
        ) : (
          /* Real card back — no count overlay */
          <img
            src={CARD_BACK_URL}
            alt={`Library (${library.length} cards)`}
            className="w-full h-full object-cover rounded-sm border border-gray-700 group-hover:border-orange-500 transition"
            draggable={false} style={{ borderRadius: CARD_CORNER_RADIUS }} />
        )}
        {/* Label */}
        <div className="absolute bottom-0.5 inset-x-0 text-center pointer-events-none">
          <span className="text-[8px] text-white/60 font-medium drop-shadow">
            Library ({library.length})
          </span>
        </div>
      </div>

      {ctxMenu && (
        <LibraryContextMenu
          x={ctxMenu.x}
          y={ctxMenu.y}
          onClose={() => setCtxMenu(null)}
        />
      )}
    </div>
  );
}

// ── GraveyardZoneBadge ────────────────────────────────────────────────────────

function GraveyardZoneBadge() {
  const graveyard = usePlaytesterStore((s) => s.graveyard);
  const setOpenPanel = usePlaytesterStore((s) => s.setOpenPanel);
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: 'graveyard' });

  // The top card of the graveyard is the last element
  const topCard = graveyard[graveyard.length - 1];
  const artUri = topCard ? getCardImageUri(topCard.card, 'large') : undefined;

  // Make the top card draggable directly off the pile
  const { setNodeRef: setDragRef, listeners, attributes, isDragging } = useDraggable({
    id: topCard ? `graveyard-pile-${topCard.instanceId}` : 'graveyard-empty',
    data: topCard ? { instanceId: topCard.instanceId, zone: 'graveyard' } : {},
    disabled: !topCard,
  });

  // Combine droppable + draggable refs on the outer container
  const combinedRef = (el: HTMLElement | null) => {
    setDropRef(el);
  };

  return (
    <div
      ref={combinedRef}
      className={`flex flex-col items-center gap-1 transition ${isOver ? 'opacity-80 ring-1 ring-red-500 rounded' : ''}`}
    >
      <div
        ref={setDragRef}
        {...listeners}
        {...attributes}
        onClick={() => setOpenPanel('graveyard')}
        className={`relative group shrink-0 cursor-pointer ${isDragging ? 'opacity-40' : ''}`}
        style={{ width: 'var(--pt-card-w)', height: 'var(--pt-card-h)' }}
        title={`Graveyard (${graveyard.length}) — drag top card`}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter') setOpenPanel('graveyard'); }}
      >
        {artUri ? (
          <img
            src={artUri}
            alt="Top of graveyard"
            className="w-full h-full object-cover rounded-sm border border-gray-700 group-hover:border-orange-500 transition opacity-80"
            draggable={false} style={{ borderRadius: CARD_CORNER_RADIUS }} />
        ) : (
          <div className="w-full h-full rounded-sm border border-gray-700 group-hover:border-orange-500 transition bg-gray-900 flex items-center justify-center">
            <span className="text-gray-600 text-xs">GY</span>
          </div>
        )}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="text-white font-black text-xl drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)]">
            {graveyard.length}
          </span>
        </div>
        <div className="absolute bottom-0.5 inset-x-0 text-center pointer-events-none">
          <span className="text-[8px] text-white/60 font-medium drop-shadow">Graveyard</span>
        </div>
      </div>
    </div>
  );
}

// ── ExileZoneBadge ────────────────────────────────────────────────────────────

function ExileZoneBadge() {
  const exile = usePlaytesterStore((s) => s.exile);
  const setOpenPanel = usePlaytesterStore((s) => s.setOpenPanel);
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: 'exile' });

  const topCard = exile[exile.length - 1];
  const artUri = topCard ? getCardImageUri(topCard.card, 'large') : undefined;

  const { setNodeRef: setDragRef, listeners, attributes, isDragging } = useDraggable({
    id: topCard ? `exile-pile-${topCard.instanceId}` : 'exile-empty',
    data: topCard ? { instanceId: topCard.instanceId, zone: 'exile' } : {},
    disabled: !topCard,
  });

  const combinedRef = (el: HTMLElement | null) => {
    setDropRef(el);
  };

  return (
    <div
      ref={combinedRef}
      className={`flex flex-col items-center gap-1 transition ${isOver ? 'opacity-80 ring-1 ring-yellow-500 rounded' : ''}`}
    >
      <div
        ref={setDragRef}
        {...listeners}
        {...attributes}
        onClick={() => setOpenPanel('exile')}
        className={`relative group shrink-0 cursor-pointer ${isDragging ? 'opacity-40' : ''}`}
        style={{ width: 'var(--pt-card-w)', height: 'var(--pt-card-h)' }}
        title={`Exile (${exile.length}) — drag top card`}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter') setOpenPanel('exile'); }}
      >
        {artUri ? (
          <img
            src={artUri}
            alt="Top of exile"
            className="w-full h-full object-cover rounded-sm border border-gray-700 group-hover:border-orange-500 transition"
            draggable={false} style={{ borderRadius: CARD_CORNER_RADIUS }} />
        ) : (
          <div className="w-full h-full rounded-sm border border-gray-700 group-hover:border-orange-500 transition bg-gray-900 flex items-center justify-center">
            <span className="text-gray-600 text-xs">Exile</span>
          </div>
        )}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="text-white font-black text-xl drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)]">
            {exile.length}
          </span>
        </div>
        <div className="absolute bottom-0.5 inset-x-0 text-center pointer-events-none">
          <span className="text-[8px] text-white/60 font-medium drop-shadow">Exile</span>
        </div>
      </div>
    </div>
  );
}

// ── HandLabel ─────────────────────────────────────────────────────────────────

function HandLabel() {
  const count = usePlaytesterStore((s) => s.hand.length);
  return (
    <span className="text-[9px] text-gray-600 px-1 shrink-0">
      Hand ({count})
    </span>
  );
}

// ── PlaytesterBottomRow ───────────────────────────────────────────────────────

export default function PlaytesterBottomRow() {
  return (
    <div className="shrink-0 bg-gray-900 border-t border-gray-800 flex items-end gap-2 px-2 py-2">
      {/* Hand — flex-1 so it absorbs available width and fans when >7 cards */}
      <div className="flex flex-col flex-1 min-w-0">
        <HandLabel />
        <HandDisplay />
      </div>

      <div className="w-px self-stretch bg-gray-800 shrink-0" />

      {/* Fixed-size zone piles */}
      <LibraryZoneBadge />
      <GraveyardZoneBadge />
      <ExileZoneBadge />

      <div className="w-px self-stretch bg-gray-800 shrink-0" />

      {/* Command zone */}
      <CommandZoneDisplay />
    </div>
  );
}
